import { Fragment, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { Swiper, SwiperSlide } from 'swiper/react';

import ArrowsLeftRightIcon from '~/assets/icons/svgs/arrows-left-right.svg?react';
import ColorInputPickerV2 from '~/components/builder/ColorInputPickerV2';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import useBuilderBlockMaxWidth from '~/hooks/useBuilderBlockMaxWidth';
import { COLORS } from '~/styles/colors';

type MultiColorPickerProps = {
  initialColors: string[][] | string[];
  names: string[];
  defaultSelectedIndex?: number;
};

const LABELS_MAP = {
  1: ['template1:primary'],
  2: ['template1:primary', 'template1:secondary'],
  3: ['template1:bg', 'template1:bgLink', 'template1:colorLink'],
};

export default function MultiColorPickerV2({ initialColors, names, defaultSelectedIndex }: MultiColorPickerProps) {
  const isSingle = names.length === 1;
  const { t } = useTranslation();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const { setValue, formState, watch } = useFormContext();

  const [colorsArray, setColorsArray] = useState(initialColors);
  const [selectedIndex, setSelectedIndex] = useState(defaultSelectedIndex ?? 0);

  const desktopContainerWidth = useBuilderBlockMaxWidth();

  const inputContainerStyles = isDesktop
    ? {}
    : { flexDirection: 'column', alignItems: names.length === 1 ? undefined : 'center' };
  const isColorError = 'design' in (formState.errors?.appearance ?? {});

  const handleSwapColorClick = () => {
    if (names.length === 2) {
      setColorsArray((prevState) =>
        prevState.map((colors, idx) => {
          if (selectedIndex === idx) {
            const [firstColor, secondColor] = colors;

            return [secondColor, firstColor];
          }

          return colors;
        }),
      );

      const firstColor = watch(names[0]);
      const secondColor = watch(names[1]);

      setValue(names[0], secondColor);
      setValue(names[1], firstColor);
    }
  };

  const handleSelectColorIndex = (index: number) => {
    setSelectedIndex(index);
    if (isSingle) {
      setValue(names[0], colorsArray[index]);
    } else {
      names.forEach((name, nameIndex) => {
        setValue(name, colorsArray[index][nameIndex]);
      });
    }
  };

  const labels = LABELS_MAP[names.length];

  const handleChangeInput = ({ value, index }: { value: string; index: number }) => {
    if (isSingle) {
      setColorsArray((prevState) =>
        prevState.map((color, idx: number) => {
          if (idx === selectedIndex) {
            return value;
          }
          return color;
        }),
      );
    } else {
      setColorsArray((prevState) =>
        prevState.map((colors, idx: number) => {
          if (selectedIndex === idx) {
            let [firstColor, secondColor, thirdColor] = colors;

            if (thirdColor) {
              if (index === 0) {
                firstColor = value;
              } else if (index === 1) {
                secondColor = value;
              } else {
                thirdColor = value;
              }

              return [firstColor, secondColor, thirdColor];
            }

            if (index === 0) {
              firstColor = value;
            } else {
              secondColor = value;
            }

            return [firstColor, secondColor];
          }

          return colors;
        }),
      );
    }
  };

  return (
    <>
      <Box sx={{ maxWidth: isDesktop ? desktopContainerWidth : 'calc(100dvw - 64px)' }}>
        <Swiper spaceBetween={8} slidesPerView="auto">
          {colorsArray?.map((item, index: number) => {
            const isSingleColor = typeof item === 'string';
            const isSelected = isSingleColor
              ? item === colorsArray[selectedIndex]
              : item?.every((element, index: number) => element === colorsArray[selectedIndex][index]) &&
                index === selectedIndex;

            return (
              <SwiperSlide key={index} style={{ maxWidth: 148, borderRadius: '20px' }}>
                <SwatchContainer key={index} onClick={() => handleSelectColorIndex(index)} $isSelected={isSelected}>
                  {isSingleColor ? (
                    <ColorSwatch $color={item} $count={1} />
                  ) : (
                    item?.map((subColor, indexSub) => (
                      <ColorSwatch key={indexSub} $color={subColor} $count={item.length} />
                    ))
                  )}
                </SwatchContainer>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </Box>
      <Box
        display="flex"
        alignItems="flex-end"
        justifyContent="space-between"
        sx={{
          backgroundColor: COLORS.lightGray,
          padding: '16px',
          gap: '16px',
          mt: 2,
          borderRadius: '20px',
          ...inputContainerStyles,
          paddingBottom: isColorError ? '36px' : '16px',
        }}
      >
        {names.map((name, indexSub, arr) => {
          return (
            <Fragment key={indexSub}>
              <ColorInputPickerV2
                name={name}
                label={t(labels[indexSub])}
                fullWidth
                absoluteHelperText
                containerSx={arr.length === 1 ? { maxWidth: '300px' } : undefined}
                onChange={(value) => handleChangeInput({ value, index: indexSub })}
              />
              {indexSub === 0 && names.length === 2 && (
                <IconButton
                  aria-label="swap"
                  sx={{
                    width: '40px',
                    height: '40px',
                    rotate: isDesktop ? undefined : '90deg',
                    backgroundColor: COLORS.primary,
                    '&:hover': {
                      backgroundColor: COLORS.primary,
                    },
                  }}
                  disableRipple
                  onClick={handleSwapColorClick}
                >
                  <ArrowsLeftRightIcon />
                </IconButton>
              )}
            </Fragment>
          );
        })}
      </Box>
    </>
  );
}

const ColorSwatch = styled(Box)<{ $color: string; $count: number }>(({ $color, $count }) => {
  return {
    display: 'flex',
    flex: 1,
    backgroundColor: $color,
    height: 28,
    minWidth: $count === 3 ? '34px' : '56px',
    boxSizing: 'border-box',
    border: $color?.toLowerCase() === '#ffffff' || $color === 'white' ? `1px solid ${COLORS.black}` : undefined,
  };
});

const SwatchContainer = styled('button')<{ $isSelected: boolean }>(({ $isSelected }) => ({
  backgroundColor: COLORS.white,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: `2px solid ${$isSelected ? COLORS.primary : COLORS.grey200}`,
  borderRadius: '4px',
  padding: '14px',
  maxWidth: 150,
  flex: 1,
  gap: '5px',
  overscrollBehavior: 'contain',
  width: '100%',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
}));
